import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Pencil, Save, FileDown, Loader2, Plus, Trash2, Info,
  ListChecks, ScrollText, ExternalLink, Maximize2, Minimize2, ZoomIn, ZoomOut, Scan,
  ChevronUp, ChevronDown, BookmarkPlus, MessageCircle, X,
} from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api, { API, fmtIDR, fmtDate } from "@/lib_app/api";
import { toast } from "sonner";
import DateInput from "@/components/app/DateInput";
import Avatar from "@/components/app/Avatar";
import { useAuth } from "@/context/AuthContext";

const UNITS = ["ls", "unit", "bln", "titik", "paket", "orang", "set", "lembar", "m2", "hari"];

export const defaultTermsGroups = () => ([
  { title: "The project will be started after the agreement is signed", items: [] },
  { title: "The price above includes:", items: ["VAT 11%"] },
  { title: "Terms of payment:", items: ["100% Full Payment after Installation/BAST"] },
]);

/** Legacy free-text terms -> [{title, items[]}] */
export function parseTermsText(text) {
  const groups = [];
  String(text || "").split(/\r?\n/).forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const isSub = /^[-•*]/.test(line) || /^\s{2,}/.test(raw);
    const clean = line.replace(/^[-•*]\s*/, "").replace(/^\d+[.)]\s*/, "").trim();
    if (!clean) return;
    if (isSub && groups.length) groups[groups.length - 1].items.push(clean);
    else groups.push({ title: clean, items: [] });
  });
  return groups;
}

const termsToText = (groups) => (groups || [])
  .map((g, i) => [`${i + 1}. ${g.title}`, ...(g.items || []).map((it) => `   - ${it}`)].join("\n"))
  .join("\n");

const onlyDigits = (v) => String(v ?? "").replace(/[^\d]/g, "");
const groupThousand = (v) => { const d = onlyDigits(v); return d ? Number(d).toLocaleString("id-ID") : ""; };

export const num = (v) => {
  const n = Number(v);
  return isFinite(n) ? n : 0;
};

const emptyItem = () => ({ description: "", qty: 1, unit: "ls", price: 0, children: [] });
const emptyGroup = () => ({ title: "", price_mode: "lump_sum", qty: 1, unit: "ls", price: 0, items: [{ description: "", children: [] }] });

export function computeTotals(groups, ppnType) {
  let subtotal = 0;
  const gsubs = (groups || []).map((g) => {
    const s = g.price_mode === "itemized"
      ? (g.items || []).reduce((acc, it) => acc + num(it.qty) * num(it.price), 0)
      : num(g.qty || 1) * num(g.price);
    subtotal += s;
    return s;
  });
  const ppn = ppnType === "ppn" ? subtotal * 0.11 : 0;
  return { gsubs, subtotal, ppn, grand: subtotal + ppn };
}

const ANGKA = ["", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "delapan", "sembilan", "sepuluh", "sebelas"];
function convertTerbilang(n) {
  if (n < 12) return [ANGKA[n]];
  if (n < 20) return [...convertTerbilang(n - 10), "belas"];
  if (n < 100) {
    const t = Math.floor(n / 10), r = n % 10;
    const parts = t === 1 ? ["sepuluh"] : [...convertTerbilang(t), "puluh"];
    return r ? [...parts, ...convertTerbilang(r)] : parts;
  }
  if (n < 200) { const r = n - 100; return r ? ["seratus", ...convertTerbilang(r)] : ["seratus"]; }
  if (n < 1000) {
    const h = Math.floor(n / 100), r = n % 100;
    const parts = [...convertTerbilang(h), "ratus"];
    return r ? [...parts, ...convertTerbilang(r)] : parts;
  }
  if (n < 2000) { const r = n - 1000; return r ? ["seribu", ...convertTerbilang(r)] : ["seribu"]; }
  if (n < 1000000) {
    const th = Math.floor(n / 1000), r = n % 1000;
    const parts = [...convertTerbilang(th), "ribu"];
    return r ? [...parts, ...convertTerbilang(r)] : parts;
  }
  if (n < 1000000000) {
    const m = Math.floor(n / 1000000), r = n % 1000000;
    const parts = [...convertTerbilang(m), "juta"];
    return r ? [...parts, ...convertTerbilang(r)] : parts;
  }
  const b = Math.floor(n / 1000000000), r = n % 1000000000;
  const parts = [...convertTerbilang(b), "miliar"];
  return r ? [...parts, ...convertTerbilang(r)] : parts;
}
export function terbilang(value) {
  const n = Math.floor(Math.abs(num(value)));
  if (n === 0) return "Nol";
  const s = convertTerbilang(n).join(" ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function toForm(d) {
  return {
    id: d.id, number: d.number, prospect_id: d.prospect_id, client_id: d.client_id, sales_id: d.sales_id,
    customer: d.customer || "", pic_name: d.pic_name || "", pic_contact: d.pic_contact || "",
    date: d.date || new Date().toISOString().slice(0, 10), valid_until: d.valid_until || "",
    ppn_type: d.ppn_type || "ppn", terms: d.terms || "", notes: d.notes || "",
    terms_groups: (d.terms_groups && d.terms_groups.length)
      ? d.terms_groups.map((g) => ({ title: g.title || "", items: [...(g.items || [])] }))
      : parseTermsText(d.terms),
    groups: (d.groups && d.groups.length ? d.groups : [emptyGroup()]).map((g) => ({
      title: g.title || "", price_mode: g.price_mode === "itemized" ? "itemized" : "lump_sum",
      qty: g.qty ?? 1, unit: g.unit || "ls", price: g.price ?? 0,
      items: (g.items && g.items.length ? g.items : [{ description: "" }]).map((it) => ({
        description: it.description || "", qty: it.qty ?? 1, unit: it.unit || "ls", price: it.price ?? 0,
        children: [...(it.children || [])],
      })),
    })),
  };
}

function defaultForm(user) {
  return {
    id: null, number: null, prospect_id: null, client_id: null, sales_id: user?.user_id || null,
    customer: "", pic_name: "", pic_contact: "",
    date: new Date().toISOString().slice(0, 10), valid_until: "",
    ppn_type: "ppn", terms_groups: defaultTermsGroups(), notes: "",
    groups: [emptyGroup()],
  };
}

export default function QuotationDetailModal({ open, quotationId, prefill, initialMode = "read", onClose, onSaved, onDeleted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [team, setTeam] = useState([]);
  const [previewFull, setPreviewFull] = useState(false);
  const [termTemplates, setTermTemplates] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadTermTemplates = () => api.get("/quotation-term-templates").then((r) => setTermTemplates(r.data || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/users").then((r) => setTeam(r.data || [])).catch(() => {});
    loadTermTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTab("info");
    if (quotationId) {
      setLoading(true);
      api.get(`/quotations/${quotationId}`)
        .then((r) => { setData(r.data); setForm(toForm(r.data)); setMode(initialMode); })
        .catch(() => toast.error("Gagal memuat quotation"))
        .finally(() => setLoading(false));
    } else {
      setData(null);
      setForm(prefill || defaultForm(user));
      setMode("edit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quotationId]);

  const totals = useMemo(() => (form ? computeTotals(form.groups, form.ppn_type) : { subtotal: 0, ppn: 0, grand: 0, gsubs: [] }), [form]);

  if (!open) return null;

  const isOwner = user?.role === "owner";
  const isDirector = user?.role === "sales_director";
  const canEdit = isOwner || isDirector || (user && (!data || data.sales_id === user.user_id));
  const isMine = user && data && data.sales_id === user.user_id;
  // Sales boleh hapus penawaran miliknya sendiri, selama belum ada invoice dari penawaran itu.
  const deleteLocked = !(isOwner || isDirector) && Number(data?.invoices_count || 0) > 0;
  const canDelete = isOwner || isDirector || (isMine && user?.role !== "finance");
  const salesUser = team.find((u) => u.user_id === form?.sales_id);
  const salesName = salesUser?.name || data?.sales_name || "";
  const salesNickname = salesUser?.nickname || salesName;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGroup = (gi, patch) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setItem = (gi, ii, patch) => setForm((f) => ({
    ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: g.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : g)),
  }));
  const addGroup = () => setForm((f) => ({ ...f, groups: [...f.groups, emptyGroup()] }));
  const removeGroup = (gi) => setForm((f) => ({ ...f, groups: f.groups.filter((_, i) => i !== gi) }));
  const addItem = (gi) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: [...g.items, emptyItem()] } : g)) }));
  const removeItem = (gi, ii) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g)) }));
  const setPriceMode = (gi, m) => setForm((f) => ({
    ...f,
    groups: f.groups.map((g, i) => {
      if (i !== gi) return g;
      if (m === "itemized") {
        return {
          ...g, price_mode: m,
          items: g.items.map((it, idx) => ({
            description: it.description || "",
            children: [...(it.children || [])],
            qty: num(it.qty) || (idx === 0 ? (num(g.qty) || 1) : 1),
            unit: it.unit || g.unit || "ls",
            price: num(it.price) || (idx === 0 ? num(g.price) : 0),
          })),
        };
      }
      const sub = g.items.reduce((acc, it) => acc + num(it.qty) * num(it.price), 0);
      return {
        ...g, price_mode: m, qty: g.qty || 1, unit: g.unit || "ls", price: sub || g.price || 0,
        items: g.items.map((it) => ({ description: it.description || "", children: [...(it.children || [])] })),
      };
    }),
  }));

  const setTermGroup = (gi, patch) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setTermItem = (gi, ii, val) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: g.items.map((t, j) => (j === ii ? val : t)) } : g)) }));
  const addTermGroup = () => setForm((f) => ({ ...f, terms_groups: [...(f.terms_groups || []), { title: "", items: [] }] }));
  const removeTermGroup = (gi) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.filter((_, i) => i !== gi) }));
  const addTermItem = (gi) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: [...(g.items || []), ""] } : g)) }));
  const removeTermItem = (gi, ii) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g)) }));
  const moveGroup = (gi, dir) => setForm((f) => {
    const arr = [...f.groups];
    const to = gi + dir;
    if (to < 0 || to >= arr.length) return f;
    [arr[gi], arr[to]] = [arr[to], arr[gi]];
    return { ...f, groups: arr };
  });
  const moveItem = (gi, ii, dir) => setForm((f) => ({
    ...f,
    groups: f.groups.map((g, i) => {
      if (i !== gi) return g;
      const arr = [...g.items];
      const to = ii + dir;
      if (to < 0 || to >= arr.length) return g;
      [arr[ii], arr[to]] = [arr[to], arr[ii]];
      return { ...g, items: arr };
    }),
  }));

  const moveTermGroup = (gi, dir) => setForm((f) => {
    const arr = [...f.terms_groups];
    const to = gi + dir;
    if (to < 0 || to >= arr.length) return f;
    [arr[gi], arr[to]] = [arr[to], arr[gi]];
    return { ...f, terms_groups: arr };
  });

  const applyTermTemplate = (tplId) => {
    const tpl = termTemplates.find((t) => t.id === tplId);
    if (!tpl) return;
    setForm((f) => ({ ...f, terms_groups: (tpl.groups || []).map((g) => ({ title: g.title || "", items: [...(g.items || [])] })) }));
    toast.success(`Template "${tpl.name}" diterapkan`);
  };

  const saveTermTemplate = async (name) => {
    const groups = (form.terms_groups || []).filter((g) => (g.title || "").trim() || (g.items || []).some((t) => (t || "").trim()));
    if (!groups.length) { toast.error("Syarat & ketentuan masih kosong"); return; }
    try {
      await api.post("/quotation-term-templates", { name: name.trim(), groups });
      await loadTermTemplates();
      toast.success("Template disimpan");
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menyimpan template");
    }
  };

  const deleteTermTemplate = async (tplId) => {
    try {
      await api.delete(`/quotation-term-templates/${tplId}`);
      await loadTermTemplates();
      toast.success("Template dihapus");
    } catch { toast.error("Gagal menghapus template"); }
  };

  const cancelEdit = () => {
    if (!data) { onClose(); return; }
    setForm(toForm(data));
    setMode("read");
  };

  const save = async () => {
    if (!form.customer.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    if (!form.id && !form.prospect_id) { toast.error("Penawaran harus terhubung ke prospek"); return; }
    setSaving(true);
    try {
      const payload = {
        customer: form.customer, pic_name: form.pic_name, pic_contact: form.pic_contact,
        date: form.date, valid_until: form.valid_until || null, ppn_type: form.ppn_type,
        terms: termsToText(form.terms_groups),
        terms_groups: (form.terms_groups || []).filter((g) => (g.title || "").trim() || (g.items || []).some((t) => (t || "").trim())),
        notes: form.notes,
        prospect_id: form.prospect_id, client_id: form.client_id, sales_id: form.sales_id,
        groups: form.groups.map((g) => ({
          title: g.title, price_mode: g.price_mode, qty: num(g.qty), unit: g.unit, price: num(g.price),
          items: g.items.map((it) => ({
            description: it.description, qty: num(it.qty), unit: it.unit, price: num(it.price),
            children: (it.children || []).map((c) => String(c || "").trim()).filter(Boolean),
          })),
        })),
      };
      let res;
      if (form.id) res = await api.patch(`/quotations/${form.id}`, payload);
      else res = await api.post("/quotations", payload);
      toast.success(form.id ? "Quotation diperbarui" : `Quotation ${res.data.number} dibuat`);
      setData(res.data);
      setForm(toForm(res.data));
      setMode("read");
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!data?.id) return;
    setConfirmDelete(false);
    try {
      await api.delete(`/quotations/${data.id}`);
      toast.success("Quotation dihapus");
      onDeleted?.(data.id);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.response?.data?.message || "Gagal menghapus"); }
  };

  const downloadPdf = () => {
    if (!data?.id) return;
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/quotations/${data.id}/pdf?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const sendWhatsApp = () => {
    if (!data?.id) return;
    const raw = form.pic_contact || "";
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits || digits.length < 9 || raw.includes("@")) {
      toast.error("Nomor WhatsApp PIC pelanggan belum valid — isi Kontak PIC dengan nomor telepon");
      return;
    }
    const phone = digits.startsWith("0") ? "62" + digits.slice(1) : digits.startsWith("62") ? digits : "62" + digits;
    const lines = [
      `Halo ${form.pic_name || form.customer},`,
      `Berikut Quotation ${form.number || ""} untuk ${form.customer} senilai ${fmtIDR(totals.grand)}.`,
      data?.public_pdf_url ? `Silakan unduh dokumen lengkap di: ${data.public_pdf_url}` : null,
      null,
      `Terima kasih,`,
      `${salesName}${company?.name ? " · " + company.name : ""}`,
    ].filter((l) => l !== null);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const tabs = [
    { key: "info", label: "Informasi", icon: Info },
    { key: "items", label: "Item & Harga", icon: ListChecks },
    { key: "terms", label: "Syarat & Ketentuan", icon: ScrollText },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-[1560px] w-[96vw] max-h-[92vh] overflow-hidden rounded-3xl border-0 p-0"
        data-testid="quotation-detail-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Quotation {data?.number || "Baru"}</DialogTitle>
        <DialogDescription className="sr-only">Detail quotation</DialogDescription>

        {loading || !form ? (
          <div className="h-[60vh] flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#6B8FE2]" />
          </div>
        ) : (
          <div className="flex h-[92vh] max-h-[92vh]">
            <div className={`${previewFull ? "hidden" : "block"} w-full lg:w-1/2 lg:shrink-0 min-w-0 overflow-y-auto p-6 space-y-5`}>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold" data-testid="quotation-modal-label">
                {form.id ? "Detail Quotation" : "Buat Quotation"}{mode === "edit" ? " · Mode Edit" : ""}
              </p>

              {/* HERO */}
              <div
                className="rounded-2xl p-5 backdrop-blur-xl"
                style={{
                  background: "linear-gradient(135deg, rgba(190,210,240,0.55), rgba(232,240,254,0.35))",
                  border: "1px solid rgba(150,175,220,0.45)",
                  boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
                }}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 font-mono">
                        {form.number || "Nomor otomatis saat disimpan"}
                      </span>
                      <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${form.ppn_type === "ppn" ? "bg-[#D8EEDF] text-[#3f7a52]" : "bg-[#DCE0E7] text-[#626061]"}`}>
                        {form.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-[#090808] leading-tight truncate" data-testid="quotation-modal-customer">{form.customer || "Pelanggan Baru"}</h2>
                    {data?.service && (
                      <p className="text-xs font-semibold text-[#3B4A63] mt-0.5 truncate" data-testid="quotation-modal-service">{data.service}</p>
                    )}
                  </div>
                  {data?.prospect_id && user?.role !== "finance" && (
                    <button
                      onClick={() => { onClose(); navigate(`/prospects?open=${data.prospect_id}`); }}
                      data-testid="quotation-open-prospect"
                      className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 shrink-0 shadow-sm gap-1.5 hover:bg-white"
                    >
                      <ExternalLink className="w-3 h-3" /> Prospek
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Avatar name={salesName} picture={salesUser?.picture} size="sm" className="!w-7 !h-7 !text-[11px]" />
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Sales</div>
                      <div className="text-sm font-semibold text-[#090808]" data-testid="quotation-hero-sales">{salesNickname || "-"}</div>
                    </div>
                  </div>
                  <div className="w-px h-8 bg-white/70 hidden sm:block" />
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Tanggal</div>
                    <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{form.date ? fmtDate(form.date) : "-"}</div>
                  </div>
                  <div className="w-px h-8 bg-white/70 hidden sm:block" />
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Berlaku Hingga</div>
                    <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{form.valid_until ? fmtDate(form.valid_until) : "—"}</div>
                  </div>
                </div>
              </div>

              {/* STAT STRIP */}
              <div className="grid grid-cols-3 rounded-2xl bg-white/70 border border-white/80 backdrop-blur-xl overflow-hidden" style={{ boxShadow: "0 6px 18px rgba(59,74,99,0.08)" }} data-testid="quotation-totals-strip">
                <div className="p-4 border-r border-[#DCE0E7]/60">
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Subtotal</div>
                  <div className="text-lg font-bold text-[#090808] font-num tabular-nums mt-1">{fmtIDR(totals.subtotal)}</div>
                </div>
                <div className="p-4 border-r border-[#DCE0E7]/60">
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">PPN 11%</div>
                  <div className="text-lg font-bold font-num tabular-nums mt-1 text-[#626061]">{form.ppn_type === "ppn" ? fmtIDR(totals.ppn) : "-"}</div>
                </div>
                <div className="p-4">
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Grand Total</div>
                  <div className="text-lg font-bold text-[#3B4A63] font-num tabular-nums mt-1">{fmtIDR(totals.grand)}</div>
                </div>
              </div>

              {/* TAB BAR */}
              <div className="flex items-center justify-between border-b border-[#DCE0E7] gap-3">
                <div className="flex items-center overflow-x-auto flex-1">
                  {tabs.map((t) => {
                    const Ico = t.icon;
                    const active = tab === t.key;
                    return (
                      <button
                        key={t.key} onClick={() => setTab(t.key)} data-testid={`quotation-tab-${t.key}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm transition-colors whitespace-nowrap border-b-2 ${
                          active ? (mode === "edit" ? "text-[#3B4A63] border-[#3B4A63] font-bold" : "text-[#090808] border-[#090808] font-bold") : "text-[#A0A3A8] border-transparent hover:text-[#626061]"
                        }`}
                      >
                        <Ico className="w-3.5 h-3.5" /> {t.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 pb-2">
                  {mode === "read" && canEdit && (
                    <IconAction icon={Pencil} label="Edit" variant="info" size="sm" onClick={() => setMode("edit")} testid="quotation-edit-toggle" />
                  )}
                  {mode === "edit" && (
                    <>
                      <IconAction icon={X} label="Batal" size="sm" onClick={cancelEdit} disabled={saving} testid="quotation-edit-cancel" />
                      <IconAction icon={Save} label={saving ? "Menyimpan…" : "Simpan"} variant="primary" size="sm" onClick={save} disabled={saving} testid="quotation-save-btn" />
                    </>
                  )}
                </div>
              </div>

              {/* TAB CONTENT */}
              {tab === "info" && (
                mode === "read" ? (
                  <div className="space-y-0 rounded-2xl bg-white/50 border border-[#E5EEFA]/60 px-5 pt-2 pb-4">
                    <InfoRow label="Pelanggan" value={form.customer || "-"} />
                    <InfoRow label="Sales" value={salesName || "-"} />
                    <InfoRow label="PIC" value={form.pic_name || "-"} />
                    <InfoRow label="Kontak" value={form.pic_contact || "-"} />
                    <InfoRow label="Tanggal" value={form.date ? fmtDate(form.date) : "-"} valueClass="font-num" />
                    <InfoRow label="Berlaku Hingga" value={form.valid_until ? fmtDate(form.valid_until) : "-"} valueClass="font-num" />
                    <InfoRow label="Pajak" value={form.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"} />
                    <div className="pt-4">
                      <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">Catatan Internal</div>
                      {form.notes ? (
                        <div className="rounded-xl bg-white/70 border border-[#DCE0E7]/70 p-3 text-sm text-[#3B4A63] whitespace-pre-wrap">{form.notes}</div>
                      ) : (
                        <div className="rounded-xl bg-[#F9FAFC] border border-[#DCE0E7]/70 p-3 text-sm text-[#A0A3A8] italic">Belum ada catatan.</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Pelanggan (QTN. To)" required>
                        <input data-testid="q-customer" value={form.customer} onChange={(e) => setField("customer", e.target.value)} className={inp} placeholder="Nama pelanggan / instansi" />
                      </Field>
                      <Field label="PIC">
                        <input data-testid="q-pic" value={form.pic_name} onChange={(e) => setField("pic_name", e.target.value)} className={inp} placeholder="Nama PIC" />
                      </Field>
                      <Field label="Sales (pemilik nomor QTN)">
                        {!form.id && (isOwner || isDirector) ? (
                          <Select value={form.sales_id || ""} onValueChange={(v) => setField("sales_id", v)}>
                            <SelectTrigger data-testid="q-sales" className="h-10 rounded-lg bg-white text-sm border-black/10">
                              <SelectValue placeholder="Pilih sales" />
                            </SelectTrigger>
                            <SelectContent>
                              {team.filter((u) => u.is_active !== false && u.role !== "finance").map((u) => (
                                <SelectItem key={u.user_id} value={u.user_id} className="text-sm">{u.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="h-10 flex items-center text-sm font-semibold text-[#090808]" data-testid="q-sales-readonly">{salesName || "-"}</div>
                        )}
                      </Field>
                      <Field label="Kontak">
                        <input data-testid="q-contact" value={form.pic_contact} onChange={(e) => setField("pic_contact", e.target.value)} className={inp} placeholder="No. telepon / email" />
                      </Field>
                      <Field label="Tanggal">
                        <DateInput data-testid="q-date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="h-10 rounded-lg bg-white" />
                      </Field>
                      <Field label="Berlaku Hingga">
                        <DateInput data-testid="q-valid-until" value={form.valid_until} onChange={(e) => setField("valid_until", e.target.value)} className="h-10 rounded-lg bg-white" />
                      </Field>
                      <Field label="Pajak">
                        <div className="flex items-center gap-2 h-10">
                          {[["ppn", "PPN 11%"], ["non_ppn", "Non-PPN"]].map(([v, l]) => (
                            <button key={v} type="button" data-testid={`q-ppn-${v}`} onClick={() => setField("ppn_type", v)}
                              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-all ${form.ppn_type === v ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-black/10 hover:border-[#3B4A63]"}`}>
                              {l}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <div className="md:col-span-2">
                        <Field label="Catatan Internal (tidak muncul di PDF)">
                          <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} className="rounded-xl bg-white" data-testid="q-notes" />
                        </Field>
                      </div>
                    </div>
                  </div>
                )
              )}

              {tab === "items" && (
                <ItemsTab
                  form={form} mode={mode} totals={totals}
                  setGroup={setGroup} setItem={setItem} addGroup={addGroup} removeGroup={removeGroup}
                  addItem={addItem} removeItem={removeItem} setPriceMode={setPriceMode}
                  moveGroup={moveGroup} moveItem={moveItem}
                />
              )}

              {tab === "terms" && (
                <TermsTab
                  groups={form.terms_groups || []} mode={mode}
                  setTermGroup={setTermGroup} setTermItem={setTermItem}
                  addTermGroup={addTermGroup} removeTermGroup={removeTermGroup}
                  addTermItem={addTermItem} removeTermItem={removeTermItem}
                  moveTermGroup={moveTermGroup}
                  templates={termTemplates}
                  onApplyTemplate={applyTermTemplate}
                  onSaveTemplate={saveTermTemplate}
                  onDeleteTemplate={deleteTermTemplate}
                />
              )}

              {/* FOOTER */}
              <div className="pt-4 border-t border-[#E5EEFA]/60 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {form.id && canDelete && (
                    <IconAction
                      icon={Trash2}
                      label={deleteLocked ? "Sudah ada invoice" : "Hapus Penawaran"}
                      variant="danger"
                      disabled={deleteLocked}
                      onClick={() => (deleteLocked ? toast.error("Penawaran ini sudah dipakai pada invoice. Minta owner untuk menghapusnya.") : setConfirmDelete(true))}
                      testid="quotation-delete-btn"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {form.id && (
                    <IconAction icon={MessageCircle} label="Kirim WhatsApp" variant="whatsapp" onClick={sendWhatsApp} testid="quotation-whatsapp-btn" />
                  )}
                  {form.id && (
                    <IconAction icon={FileDown} label="Unduh PDF" variant="success" onClick={downloadPdf} testid="quotation-download-btn" />
                  )}
                  <IconAction icon={X} label="Tutup" onClick={onClose} testid="quotation-modal-close" />
                </div>
              </div>
            </div>

            {/* LIVE PDF PREVIEW */}
            <div className="hidden lg:flex flex-1 min-w-0 border-l border-[#DCE0E7]/60 bg-[#E9EDF4]">
              <PdfPreview
                form={form} totals={totals} company={company}
                salesName={team.find((u) => u.user_id === form.sales_id)?.name || data?.sales_name || user?.name}
                salesPhone={team.find((u) => u.user_id === form.sales_id)?.phone || data?.sales_phone}
                salesJobTitle={team.find((u) => u.user_id === form.sales_id)?.job_title || data?.sales_job_title}
                qrDataUri={data?.qr_data_uri || null}
                full={previewFull} onToggleFull={() => setPreviewFull((v) => !v)}
              />
            </div>
          </div>
        )}
      </DialogContent>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-3xl" data-testid="quotation-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus penawaran ini?</AlertDialogTitle>
            <AlertDialogDescription>
              Penawaran <span className="font-mono font-semibold">{data?.number}</span> beserta seluruh item dan syaratnya akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quotation-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="quotation-delete-confirm-btn" className="bg-[#CE6968] hover:bg-[#b95958]">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function MoneyInput({ value, onChange, ...rest }) {
  return (
    <input
      inputMode="numeric"
      value={groupThousand(value)}
      onChange={(e) => onChange(onlyDigits(e.target.value))}
      {...rest}
    />
  );
}

export function TermsTab({ groups, mode, setTermGroup, setTermItem, addTermGroup, removeTermGroup, addTermItem, removeTermItem, moveTermGroup, templates = [], onApplyTemplate, onSaveTemplate, onDeleteTemplate }) {
  const [tplId, setTplId] = useState("");
  const [dialog, setDialog] = useState(null); // "save" | "delete"
  const [tplName, setTplName] = useState("");
  const selected = templates.find((t) => t.id === tplId);
  if (mode === "read") {
    return (
      <div className="rounded-2xl bg-white/50 border border-[#E5EEFA]/60 p-5" data-testid="quotation-terms-read">
        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-3">Syarat &amp; Ketentuan</div>
        {groups.length === 0 ? (
          <div className="text-sm text-[#A0A3A8] italic">Belum ada syarat &amp; ketentuan.</div>
        ) : (
          <ol className="space-y-2">
            {groups.map((g, gi) => (
              <li key={gi} className="text-sm text-[#3B4A63]">
                <div className="flex gap-2">
                  <span className="font-bold text-[#090808] shrink-0">{gi + 1}.</span>
                  <span className="font-semibold text-[#090808]">{g.title}</span>
                </div>
                {(g.items || []).length > 0 && (
                  <ul className="mt-1 ml-6 space-y-0.5">
                    {g.items.map((it, ii) => (
                      <li key={ii} className="text-[13px] text-[#626061]">- {it}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="quotation-terms-edit">
      <div className="rounded-2xl bg-white/80 border border-[#DCE0E7] p-3 flex flex-wrap items-center gap-2" data-testid="q-term-template-bar">
        <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold inline-flex items-center gap-1.5">
          <BookmarkPlus className="w-3.5 h-3.5 text-[#3B4A63]" /> Template
        </span>
        <Select value={tplId} onValueChange={setTplId}>
          <SelectTrigger data-testid="q-term-template-select" className="h-9 min-w-[200px] rounded-lg bg-white border-[#DCE0E7] text-xs">
            <SelectValue placeholder={templates.length ? "Pilih template…" : "Belum ada template"} />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <button
          onClick={() => tplId && onApplyTemplate(tplId)} disabled={!tplId} data-testid="q-term-template-apply"
          className="h-9 px-3 rounded-full text-xs font-semibold bg-[#E5EEFA] text-[#3B4A63] hover:bg-[#D3E1F5] disabled:opacity-40"
        >
          Terapkan
        </button>
        {tplId && (
          <button onClick={() => setDialog("delete")} data-testid="q-term-template-delete" title="Hapus template"
            className="w-9 h-9 rounded-full text-[#CE6968] hover:bg-[#FDE7E9] inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
        )}
        <button
          onClick={() => { setTplName(""); setDialog("save"); }} data-testid="q-term-template-save"
          className="h-9 px-3 rounded-full text-xs font-semibold bg-[#090808] text-white hover:bg-black ml-auto inline-flex items-center gap-1.5"
        >
          <BookmarkPlus className="w-3.5 h-3.5" /> Simpan sebagai Template
        </button>
      </div>

      <Dialog open={dialog !== null} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-md rounded-3xl border-0 p-6" data-testid="q-term-template-dialog">
          <DialogTitle className="text-lg font-bold text-[#090808]">
            {dialog === "delete" ? "Hapus Template" : "Simpan sebagai Template"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A0A3A8]">
            {dialog === "delete"
              ? `Template "${selected?.name || ""}" akan dihapus permanen.`
              : "Syarat & ketentuan saat ini akan disimpan agar bisa dipakai ulang di penawaran lain."}
          </DialogDescription>
          {dialog === "save" && (
            <input
              autoFocus value={tplName} onChange={(e) => setTplName(e.target.value)}
              data-testid="q-term-template-name"
              placeholder="Nama template (mis. Standar Proyek)"
              className="w-full h-11 px-4 rounded-xl bg-white border border-[#DCE0E7] text-sm outline-none focus:ring-2 focus:ring-[#97ADD4]/40"
            />
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setDialog(null)} data-testid="q-term-template-dialog-cancel"
              className="h-10 px-4 rounded-full text-sm font-semibold text-[#626061] hover:bg-[#F4F5F7]">Batal</button>
            {dialog === "delete" ? (
              <button
                onClick={() => { onDeleteTemplate(tplId); setTplId(""); setDialog(null); }}
                data-testid="q-term-template-dialog-confirm"
                className="h-10 px-5 rounded-full text-sm font-semibold bg-[#CE6968] text-white hover:bg-[#b95958]"
              >Hapus</button>
            ) : (
              <button
                onClick={() => { if (!tplName.trim()) return; onSaveTemplate(tplName); setDialog(null); }}
                disabled={!tplName.trim()}
                data-testid="q-term-template-dialog-confirm"
                className="h-10 px-5 rounded-full text-sm font-semibold bg-[#090808] text-white hover:bg-black disabled:opacity-40"
              >Simpan</button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {groups.map((g, gi) => (
        <div key={gi} className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-4" data-testid={`q-term-group-${gi}`}>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 shrink-0 rounded-full bg-[#3B4A63] text-white text-xs font-bold flex items-center justify-center">{gi + 1}</span>
            <input
              value={g.title} onChange={(e) => setTermGroup(gi, { title: e.target.value })}
              data-testid={`q-term-title-${gi}`} placeholder="Judul syarat (mis. Terms of payment:)"
              className="flex-1 h-9 px-3 rounded-lg bg-white border border-[#DCE0E7] text-sm font-semibold outline-none focus:ring-2 focus:ring-[#97ADD4]/40"
            />
            <button onClick={() => moveTermGroup(gi, -1)} disabled={gi === 0} data-testid={`q-term-up-${gi}`} title="Naik"
              className="w-8 h-8 rounded-lg hover:bg-white text-[#3B4A63] flex items-center justify-center shrink-0 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
            <button onClick={() => moveTermGroup(gi, 1)} disabled={gi === groups.length - 1} data-testid={`q-term-down-${gi}`} title="Turun"
              className="w-8 h-8 rounded-lg hover:bg-white text-[#3B4A63] flex items-center justify-center shrink-0 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
            <button onClick={() => removeTermGroup(gi)} data-testid={`q-term-remove-${gi}`} title="Hapus"
              className="w-8 h-8 rounded-lg hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>

          <div className="mt-2 pl-8 space-y-1.5">
            {(g.items || []).map((it, ii) => (
              <div key={ii} className="flex items-center gap-1.5" data-testid={`q-term-item-${gi}-${ii}`}>
                <span className="text-xs text-[#A0A3A8] font-bold w-2 shrink-0">-</span>
                <input value={it} onChange={(e) => setTermItem(gi, ii, e.target.value)} className={inpXs} placeholder="Sub-poin (mis. VAT 11%)" />
                <button onClick={() => removeTermItem(gi, ii)} data-testid={`q-term-item-remove-${gi}-${ii}`}
                  className="w-7 h-7 rounded hover:bg-[#FDE7E9] text-[#CE6968] inline-flex items-center justify-center shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={() => addTermItem(gi)} data-testid={`q-term-item-add-${gi}`} className="text-xs font-semibold text-[#3B4A63] hover:underline inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Tambah Sub-poin
            </button>
          </div>
        </div>
      ))}
      <button onClick={addTermGroup} data-testid="q-term-group-add" className="w-full h-10 rounded-xl border border-dashed border-[#B0C7EA] text-sm font-semibold text-[#3B4A63] hover:border-[#3B4A63] inline-flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Tambah Syarat
      </button>
    </div>
  );
}

const MoveBtns = ({ up, down, disabledUp, disabledDown, testidUp, testidDown, size = "sm" }) => {
  const cls = size === "sm"
    ? "w-7 h-7 rounded hover:bg-white text-[#3B4A63] inline-flex items-center justify-center shrink-0 disabled:opacity-25"
    : "w-8 h-8 rounded-lg hover:bg-white text-[#3B4A63] inline-flex items-center justify-center shrink-0 disabled:opacity-25";
  const ico = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  return (
    <>
      <button onClick={up} disabled={disabledUp} data-testid={testidUp} title="Naik" className={cls}><ChevronUp className={ico} /></button>
      <button onClick={down} disabled={disabledDown} data-testid={testidDown} title="Turun" className={cls}><ChevronDown className={ico} /></button>
    </>
  );
};

/** Level 3 (detail sub-item): deskripsi saja, tanpa qty/harga. */
function ChildRows({ gi, ii, item, setItem }) {
  const kids = item.children || [];
  const set = (arr) => setItem(gi, ii, { children: arr });
  return (
    <div className="mt-1 ml-3 pl-3 border-l border-[#DCE0E7] space-y-1" data-testid={`q-sub-items-${gi}-${ii}`}>
      {kids.map((c, ci) => (
        <div key={ci} className="flex items-center gap-1.5">
          <span className="text-[#A0A3A8] text-sm leading-none w-2 shrink-0">&middot;</span>
          <input
            value={c}
            onChange={(e) => set(kids.map((x, j) => (j === ci ? e.target.value : x)))}
            data-testid={`q-sub-item-${gi}-${ii}-${ci}`}
            className={inpXs} placeholder="Detail / spesifikasi (tanpa harga)"
          />
          <MoveBtns
            up={() => { const a = [...kids]; [a[ci - 1], a[ci]] = [a[ci], a[ci - 1]]; set(a); }}
            down={() => { const a = [...kids]; [a[ci + 1], a[ci]] = [a[ci], a[ci + 1]]; set(a); }}
            disabledUp={ci === 0} disabledDown={ci === kids.length - 1}
            testidUp={`q-sub-item-up-${gi}-${ii}-${ci}`} testidDown={`q-sub-item-down-${gi}-${ii}-${ci}`}
          />
          <button
            onClick={() => set(kids.filter((_, j) => j !== ci))}
            data-testid={`q-sub-item-remove-${gi}-${ii}-${ci}`}
            className="w-7 h-7 rounded hover:bg-[#FDE7E9] text-[#CE6968] inline-flex items-center justify-center shrink-0"
          ><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
      <button
        onClick={() => set([...kids, ""])}
        data-testid={`q-sub-item-add-${gi}-${ii}`}
        className="text-[11px] font-semibold text-[#6B7A94] hover:underline inline-flex items-center gap-1"
      ><Plus className="w-3 h-3" /> Tambah Detail</button>
    </div>
  );
}

export function ItemsTab({ form, mode, totals, setGroup, setItem, addGroup, removeGroup, addItem, removeItem, setPriceMode, moveGroup, moveItem }) {
  if (mode === "read") {
    return (
      <div className="space-y-3" data-testid="quotation-items-read">
        {form.groups.map((g, gi) => (
          <div key={gi} className="rounded-2xl bg-white/50 border border-[#E5EEFA]/60 p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 shrink-0 rounded-full bg-[#3B4A63] text-white text-xs font-bold flex items-center justify-center">{gi + 1}</span>
                <span className="text-sm font-bold text-[#090808]">{g.title || "-"}</span>
              </div>
              <span className="text-xs font-bold text-[#3B4A63] font-num tabular-nums">{fmtIDR(totals.gsubs[gi] || 0)}</span>
            </div>
            {g.price_mode === "itemized" ? (
              <div className="space-y-1 pl-8">
                {g.items.map((it, ii) => (
                  <div key={ii}>
                    <div className="flex items-center justify-between text-xs text-[#626061] gap-2">
                      <span className="truncate">{it.description || "-"} <span className="text-[#A0A3A8]">({num(it.qty)} {it.unit})</span></span>
                      <span className="font-num shrink-0">{fmtIDR(num(it.qty) * num(it.price))}</span>
                    </div>
                    {(it.children || []).filter((c) => (c || "").trim()).map((c, ci) => (
                      <div key={ci} className="text-[11px] text-[#A0A3A8] pl-4">&middot; {c}</div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="pl-8 space-y-1">
                <div className="text-[11px] text-[#A0A3A8]">{num(g.qty)} {g.unit} &times; {fmtIDR(g.price)} (Lump Sum)</div>
                {g.items.filter((it) => it.description?.trim() || (it.children || []).some((c) => (c || "").trim())).map((it, ii) => (
                  <div key={ii}>
                    {it.description?.trim() && <div className="text-xs text-[#626061]">&ndash; {it.description}</div>}
                    {(it.children || []).filter((c) => (c || "").trim()).map((c, ci) => (
                      <div key={ci} className="text-[11px] text-[#A0A3A8] pl-4">&middot; {c}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="quotation-items-edit">
      {form.groups.map((g, gi) => (
        <div key={gi} className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-4" data-testid={`q-group-${gi}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 shrink-0 rounded-full bg-[#3B4A63] text-white text-xs font-bold flex items-center justify-center">{gi + 1}</span>
            <input value={g.title} onChange={(e) => setGroup(gi, { title: e.target.value })} data-testid={`q-group-title-${gi}`}
              className="flex-1 h-9 px-3 rounded-lg bg-white border border-[#DCE0E7] text-sm font-semibold outline-none focus:ring-2 focus:ring-[#97ADD4]/40" placeholder="Judul item / grup (mis. AI Candid Camera)" />
            {form.groups.length > 1 && (
              <MoveBtns
                size="md"
                up={() => moveGroup(gi, -1)} down={() => moveGroup(gi, 1)}
                disabledUp={gi === 0} disabledDown={gi === form.groups.length - 1}
                testidUp={`q-group-up-${gi}`} testidDown={`q-group-down-${gi}`}
              />
            )}
            {form.groups.length > 1 && (
              <button onClick={() => removeGroup(gi)} data-testid={`q-group-remove-${gi}`} className="w-8 h-8 rounded-lg hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>

          <div className="flex items-center gap-2 mb-3">
            {[["lump_sum", "Lump Sum"], ["itemized", "Rincian Item"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`q-mode-${v}-${gi}`} onClick={() => setPriceMode(gi, v)}
                className={`h-7 px-3 rounded-full text-[11px] font-semibold border transition-all ${g.price_mode === v ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-black/10 hover:border-[#3B4A63]"}`}>
                {l}
              </button>
            ))}
            <span className="text-[10px] text-[#A0A3A8] ml-1">{g.price_mode === "lump_sum" ? "Harga di level grup, item di bawah hanya deskripsi lingkup." : "Harga per baris item."}</span>
          </div>

          {g.price_mode === "lump_sum" ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div>
                  <span className="text-[10px] text-[#A0A3A8] font-semibold">Qty</span>
                  <input type="number" min="0" value={g.qty} onChange={(e) => setGroup(gi, { qty: e.target.value })} className={inpXs + " h-9"} data-testid={`q-group-qty-${gi}`} />
                </div>
                <div>
                  <span className="text-[10px] text-[#A0A3A8] font-semibold">Satuan</span>
                  <input list="q-units" value={g.unit} onChange={(e) => setGroup(gi, { unit: e.target.value })} className={inpXs + " h-9"} placeholder="ls" data-testid={`q-group-unit-${gi}`} />
                </div>
                <div>
                  <span className="text-[10px] text-[#A0A3A8] font-semibold">Harga (Rp)</span>
                  <MoneyInput value={g.price} onChange={(v) => setGroup(gi, { price: v })} className={inpXs + " h-9 text-right font-num tabular-nums"} data-testid={`q-group-price-${gi}`} />
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] text-[#A0A3A8] font-semibold uppercase tracking-wide">Deskripsi Lingkup Kerja (scope)</span>
                {g.items.map((it, ii) => (
                  <div key={ii} data-testid={`q-item-${gi}-${ii}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-[#A0A3A8] font-bold w-2 shrink-0">&ndash;</span>
                      <input value={it.description} onChange={(e) => setItem(gi, ii, { description: e.target.value })} className={inpXs} placeholder="Contoh: 2 unit kamera candid AI" />
                      {g.items.length > 1 && (
                        <>
                          <MoveBtns
                            up={() => moveItem(gi, ii, -1)} down={() => moveItem(gi, ii, 1)}
                            disabledUp={ii === 0} disabledDown={ii === g.items.length - 1}
                            testidUp={`q-item-up-${gi}-${ii}`} testidDown={`q-item-down-${gi}-${ii}`}
                          />
                          <button onClick={() => removeItem(gi, ii)} data-testid={`q-item-remove-${gi}-${ii}`} className="w-7 h-7 rounded hover:bg-[#FDE7E9] text-[#CE6968] inline-flex items-center justify-center shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </div>
                    <ChildRows gi={gi} ii={ii} item={it} setItem={setItem} />
                  </div>
                ))}
                <button onClick={() => addItem(gi)} data-testid={`q-item-add-${gi}`} className="text-xs font-semibold text-[#3B4A63] hover:underline inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Baris Scope</button>
              </div>
            </>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-xs">
                <thead>
                  <tr className="text-[#A0A3A8] text-left">
                    <th className="py-1 font-medium">Deskripsi</th>
                    <th className="py-1 font-medium w-16">Qty</th>
                    <th className="py-1 font-medium w-24">Satuan</th>
                    <th className="py-1 font-medium w-36">Harga (Rp)</th>
                    <th className="py-1 font-medium w-32 text-right">Total</th>
                    <th className="py-1 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((it, ii) => (
                    <Fragment key={ii}>
                    <tr data-testid={`q-item-${gi}-${ii}`}>
                      <td className="py-1 pr-1"><input value={it.description} onChange={(e) => setItem(gi, ii, { description: e.target.value })} className={inpXs} placeholder="Rincian" /></td>
                      <td className="py-1 pr-1"><input type="number" min="0" value={it.qty} onChange={(e) => setItem(gi, ii, { qty: e.target.value })} className={inpXs} /></td>
                      <td className="py-1 pr-1"><input list="q-units" value={it.unit} onChange={(e) => setItem(gi, ii, { unit: e.target.value })} className={inpXs} placeholder="ls" /></td>
                      <td className="py-1 pr-1"><MoneyInput value={it.price} onChange={(v) => setItem(gi, ii, { price: v })} className={inpXs + " text-right font-num tabular-nums"} data-testid={`q-item-price-${gi}-${ii}`} /></td>
                      <td className="py-1 text-right font-semibold whitespace-nowrap">{fmtIDR(num(it.qty) * num(it.price))}</td>
                      <td className="py-1 text-right whitespace-nowrap">
                        {g.items.length > 1 && (
                          <>
                            <MoveBtns
                              up={() => moveItem(gi, ii, -1)} down={() => moveItem(gi, ii, 1)}
                              disabledUp={ii === 0} disabledDown={ii === g.items.length - 1}
                              testidUp={`q-item-up-${gi}-${ii}`} testidDown={`q-item-down-${gi}-${ii}`}
                            />
                            <button onClick={() => removeItem(gi, ii)} data-testid={`q-item-remove-${gi}-${ii}`} className="w-7 h-7 rounded hover:bg-[#FDE7E9] text-[#CE6968] inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={6} className="pb-1">
                        <ChildRows gi={gi} ii={ii} item={it} setItem={setItem} />
                      </td>
                    </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
              <button onClick={() => addItem(gi)} data-testid={`q-item-add-${gi}`} className="text-xs font-semibold text-[#3B4A63] hover:underline inline-flex items-center gap-1 mt-1.5"><Plus className="w-3.5 h-3.5" /> Tambah Baris</button>
            </div>
          )}

          <div className="flex items-center justify-end mt-2">
            <div className="text-xs text-[#626061]">Subtotal ({gi + 1}): <b className="text-[#090808]">{fmtIDR(totals.gsubs[gi] || 0)}</b></div>
          </div>
        </div>
      ))}
      <datalist id="q-units">{UNITS.map((u) => <option key={u} value={u} />)}</datalist>
      <button onClick={addGroup} data-testid="q-group-add" className="w-full h-10 rounded-xl border border-dashed border-[#B0C7EA] text-sm font-semibold text-[#3B4A63] hover:border-[#3B4A63] inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Tambah Grup</button>
    </div>
  );
}

/* ============ PDF-IDENTICAL A4 LIVE PREVIEW ============ */
const kidLines = (it) => (it.children || []).filter((c) => (c || "").trim());
const KidRows = ({ item }) => (
  <>
    {kidLines(item).map((c, ci) => (
      <div key={ci} style={{ display: "flex", gap: 4, marginTop: 1.5, paddingLeft: 14 }}>
        <span style={{ color: "#9AA0AC", fontSize: 9, lineHeight: 1.45, flexShrink: 0 }}>&middot;</span>
        <span style={{ color: "#9AA0AC", fontSize: 9, lineHeight: 1.45 }}>{c}</span>
      </div>
    ))}
  </>
);

const A4_W = 794;   // A4 width @96dpi
const A4_H = 1123;  // A4 height @96dpi
const PAGE_PAD = 36;
const NAVY = "#3B4A63";
const LEGAL_NAME = "PT IPTEC DIGITAL SOLUTION";

const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dMY = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return String(iso);
  return `${d} ${MONTHS_EN[Number(m) - 1] || m} ${y}`;
};
const fmtQty = (v) => num(v).toFixed(2).replace(/0+$/, "").replace(/\.$/, "").replace(".", ",");

const sMetaLbl = { fontSize: 10, color: "#8A8F98", padding: "1.5px 0", width: 100 };
const sMetaVal = { fontSize: 10, fontWeight: 700, padding: "1.5px 0" };
const sBox = { background: "#F4F6FA", padding: "13px 15px", verticalAlign: "top", width: "50%", borderRadius: 10 };
const sBoxTtl = { display: "inline-block", background: "#fff", borderRadius: 20, padding: "5px 12px 4px", fontSize: 8, lineHeight: 1, letterSpacing: 1.6, color: NAVY, fontWeight: 700, marginBottom: 9 };
const sKvK = { fontSize: 10, color: "#8A8F98", padding: "2px 0", verticalAlign: "top", width: 70 };
const sKvV = { fontSize: 10, padding: "2px 0", verticalAlign: "top" };
const sTh = { background: NAVY, color: "#fff", fontSize: 8.5, letterSpacing: 1.2, padding: 10, textAlign: "left" };
const sTd = { padding: "9px 10px", fontSize: 10.5, borderBottom: "1px solid #E7EAF0", verticalAlign: "top" };
const sTdR = { ...sTd, textAlign: "right", whiteSpace: "nowrap" };
const sTotLbl = { fontSize: 10.5, color: "#626873", padding: "4px 0" };
const sTotVal = { fontSize: 10.5, fontWeight: 700, padding: "4px 0", textAlign: "right", whiteSpace: "nowrap" };

function PdfPreview({ form, totals, company, salesName, salesPhone, salesJobTitle, qrDataUri, full, onToggleFull }) {
  const wrapRef = useRef(null);
  const sheetRef = useRef(null);
  const [zoom, setZoom] = useState(0.8);
  const [scale, setScale] = useState(0.8);
  const [wrapW, setWrapW] = useState(0);
  const [sheetH, setSheetH] = useState(A4_H);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const avail = el.clientWidth - 32;
      setWrapW(el.clientWidth);
      setScale(zoom === "fit" ? Math.max(0.2, Math.min(1, avail / A4_W)) : zoom);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoom, full]);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSheetH(Math.max(A4_H, el.offsetHeight)));
    ro.observe(el);
    return () => ro.disconnect();
  });

  const step = (d) => setZoom((z) => {
    const cur = z === "fit" ? scale : z;
    return Math.max(0.3, Math.min(1.4, Math.round((cur + d) * 20) / 20));
  });

  const companyName = company?.name || "Sales Pipeline";
  const groups = form.groups || [];
  const termGroups = form.terms_groups || [];
  const volCell = (qty, unit, bg) => (
    <td style={{ ...sTd, background: bg, padding: "9px 4px" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody><tr>
        <td style={{ width: "50%", textAlign: "right", fontSize: 10.5, padding: 0, whiteSpace: "nowrap" }}>{fmtQty(qty)}</td>
        <td style={{ width: "50%", textAlign: "left", fontSize: 10.5, padding: "0 0 0 4px", whiteSpace: "nowrap" }}>{unit}</td>
      </tr></tbody></table>
    </td>
  );
  const rows = [];
  let alt = false;
  groups.forEach((g, gi) => {
    const gsub = totals.gsubs[gi] || 0;
    const bg = alt ? "#F7F8FB" : "transparent";
    alt = !alt;
    const cell = (extra = {}) => ({ ...sTd, background: bg, ...extra });

    if (g.price_mode !== "itemized") {
      const subs = (g.items || []).filter((it) => (it.description || "").trim() || kidLines(it).length > 0);
      rows.push(
        <tr key={`g${gi}`}>
          <td style={{ ...cell(), textAlign: "center" }}>{gi + 1}</td>
          <td style={cell()}>
            <b style={{ fontSize: 11 }}>{g.title}</b>
            {subs.length > 0 && (
              <div style={{ marginTop: 2 }}>
                {subs.map((it, ii) => (
                  <div key={ii}>
                    {(it.description || "").trim() && (
                      <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                        <span style={{ color: "#7A8090", fontSize: 9.5, lineHeight: 1.5, flexShrink: 0 }}>&ndash;</span>
                        <span style={{ color: "#7A8090", fontSize: 9.5, lineHeight: 1.5 }}>{it.description}</span>
                      </div>
                    )}
                    <KidRows item={it} />
                  </div>
                ))}
              </div>
            )}
          </td>
          {volCell(g.qty, g.unit, bg)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(g.price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(gsub)}</td>
        </tr>
      );
      return;
    }

    const items = g.items || [];
    const single = items.length === 1 && !(items[0].description || "").trim();
    if (single) {
      rows.push(
        <tr key={`g${gi}`}>
          <td style={{ ...cell(), textAlign: "center" }}>{gi + 1}</td>
          <td style={cell()}><b style={{ fontSize: 11 }}>{g.title}</b><KidRows item={items[0]} /></td>
          {volCell(items[0].qty, items[0].unit, bg)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(items[0].price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(num(items[0].qty) * num(items[0].price))}</td>
        </tr>
      );
      return;
    }

    rows.push(
      <tr key={`g${gi}`}>
        <td style={{ ...cell(), textAlign: "center" }}>{gi + 1}</td>
        <td style={cell()} colSpan={4}><b style={{ fontSize: 11 }}>{g.title}</b></td>
      </tr>
    );
    items.forEach((it, ii) => {
      rows.push(
        <tr key={`g${gi}i${ii}`}>
          <td style={cell()}></td>
          <td style={cell()}>{it.description}<KidRows item={it} /></td>
          {volCell(it.qty, it.unit, bg)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(it.price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(num(it.qty) * num(it.price))}</td>
        </tr>
      );
    });
    rows.push(
      <tr key={`g${gi}sub`}>
        <td style={{ ...sTdR, background: "#EFF2F8", fontSize: 9.5, color: "#4A5160" }} colSpan={4}>Subtotal ({gi + 1})</td>
        <td style={{ ...sTdR, background: "#EFF2F8", fontSize: 9.5 }}><b>{fmtIDR(gsub)}</b></td>
      </tr>
    );
  });

  return (
    <div className="flex flex-col w-full min-w-0" data-testid="quotation-live-preview">
      <div className="flex items-center justify-between gap-2 pl-4 pr-14 h-12 shrink-0 border-b border-[#DCE0E7] bg-white/70 backdrop-blur">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B8FE2] font-bold">Preview PDF · A4</div>
        <div className="flex items-center gap-1">
          <button onClick={() => step(-0.1)} data-testid="preview-zoom-out" title="Perkecil" className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><ZoomOut className="w-4 h-4" /></button>
          <span className="text-[11px] font-bold text-[#3B4A63] font-num tabular-nums w-11 text-center" data-testid="preview-zoom-label">{Math.round(scale * 100)}%</span>
          <button onClick={() => step(0.1)} data-testid="preview-zoom-in" title="Perbesar" className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => setZoom("fit")} data-testid="preview-zoom-fit" title="Sesuaikan lebar" className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${zoom === "fit" ? "bg-[#3B4A63] text-white" : "hover:bg-[#E5EEFA] text-[#3B4A63]"}`}><Scan className="w-4 h-4" /></button>
          <div className="w-px h-5 bg-[#DCE0E7] mx-1" />
          <button onClick={onToggleFull} data-testid="preview-toggle-full" title={full ? "Tampilkan form" : "Lebar penuh"} className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center">
            {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div ref={wrapRef} className="flex-1 overflow-auto p-4">
        <div
          style={{ width: A4_W * scale, height: sheetH * scale }}
          className={A4_W * scale <= wrapW - 32 ? "mx-auto" : ""}
        >
          <div
            ref={sheetRef}
            data-testid="quotation-preview-sheet"
            style={{
              width: A4_W, minHeight: A4_H, padding: PAGE_PAD, background: "#fff",
              transform: `scale(${scale})`, transformOrigin: "top left",
              fontFamily: "'DM Sans', Arial, Helvetica, sans-serif", color: "#14161F", fontSize: 10.5, lineHeight: 1.4,
              boxShadow: "0 12px 40px -12px rgba(20,22,31,0.35)",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Header */}
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <tbody><tr>
                <td style={{ verticalAlign: "top", width: "58%" }}>
                  <h1 style={{ fontSize: 26, fontWeight: 700, color: NAVY, margin: "0 0 12px", letterSpacing: 0.4 }}>Quotation</h1>
                  <table style={{ borderCollapse: "collapse" }}>
                    <tbody>
                      <tr><td style={sMetaLbl}>Quotation No.</td><td style={sMetaVal}>{form.number || "—"}</td></tr>
                      <tr><td style={sMetaLbl}>Quotation Date</td><td style={sMetaVal}>{dMY(form.date)}</td></tr>
                      <tr><td style={sMetaLbl}>Valid Until</td><td style={sMetaVal}>{dMY(form.valid_until)}</td></tr>
                    </tbody>
                  </table>
                </td>
                <td style={{ verticalAlign: "top", textAlign: "right" }}>
                  {company?.logo
                    ? <img src={company.logo} alt="logo" style={{ maxHeight: 74, maxWidth: 270, borderRadius: 10, display: "inline-block", marginLeft: "auto" }} />
                    : (
                      <>
                        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.6 }}>{companyName}</div>
                        <div style={{ fontSize: 8, color: "#8A8F98", letterSpacing: 2.4, marginTop: 3 }}>QUOTATION DOCUMENT</div>
                      </>
                    )}
                </td>
              </tr></tbody>
            </table>

            {/* Info boxes */}
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "14px 0", marginTop: 18, marginLeft: -14, marginRight: -14 }}>
              <tbody><tr>
                <td style={sBox}>
                  <div style={sBoxTtl}>QUOTATION BY</div>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                    <tr><td style={sKvK}>Company</td><td style={sKvV}><b>{companyName}</b></td></tr>
                    <tr><td style={sKvK}>PIC</td><td style={sKvV}>{salesName || "-"}</td></tr>
                    <tr><td style={sKvK}>Contact</td><td style={sKvV}>{salesPhone || "-"}</td></tr>
                  </tbody></table>
                </td>
                <td style={sBox}>
                  <div style={sBoxTtl}>QUOTATION TO</div>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                    <tr><td style={sKvK}>Customer</td><td style={sKvV}><b>{form.customer || "-"}</b></td></tr>
                    <tr><td style={sKvK}>PIC</td><td style={sKvV}>{form.pic_name || "-"}</td></tr>
                    <tr><td style={sKvK}>Contact</td><td style={sKvV}>{form.pic_contact || "-"}</td></tr>
                  </tbody></table>
                </td>
              </tr></tbody>
            </table>

            {/* Items */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 20 }}>
              <thead><tr>
                <th style={{ ...sTh, width: 26, textAlign: "center" }}>#</th>
                <th style={sTh}>ITEM DESCRIPTION</th>
                <th style={{ ...sTh, width: 78, textAlign: "center" }}>VOLUME</th>
                <th style={{ ...sTh, width: 104, textAlign: "right" }}>RATE</th>
                <th style={{ ...sTh, width: 110, textAlign: "right" }}>AMOUNT</th>
              </tr></thead>
              <tbody>{rows}</tbody>
            </table>

            {/* Terms + totals */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 22 }}>
              <tbody><tr>
                <td style={{ width: "56%", verticalAlign: "top", paddingRight: 26 }}>
                  {termGroups.length > 0 && (
                    <>
                      <h3 style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, margin: "0 0 7px", letterSpacing: 0.3 }}>Terms and Conditions</h3>
                      {termGroups.map((g, gi) => (
                        <Fragment key={gi}>
                          <div style={{ fontSize: 10, lineHeight: 1.5, marginBottom: 2, display: "flex", gap: 0 }}>
                            <span style={{ width: 16, flexShrink: 0, color: NAVY, fontWeight: 700 }}>{gi + 1}.</span>
                            <span>{g.title}</span>
                          </div>
                          {(g.items || []).map((it, ii) => (
                            <div key={ii} style={{ display: "flex", gap: 4, paddingLeft: 16 }} data-testid={`preview-term-item-${gi}-${ii}`}>
                              <span style={{ color: "#5B616E", fontSize: 9.5, lineHeight: 1.5, flexShrink: 0 }} data-testid={`preview-term-dash-${gi}-${ii}`}>&ndash;</span>
                              <span style={{ color: "#5B616E", fontSize: 9.5, lineHeight: 1.5 }} data-testid={`preview-term-text-${gi}-${ii}`}>{it}</span>
                            </div>
                          ))}
                        </Fragment>
                      ))}
                    </>
                  )}
                  {(form.notes || "").trim() && (
                    <div style={{ marginTop: 16 }}>
                      <h3 style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, margin: "0 0 7px", letterSpacing: 0.3 }}>Additional Notes</h3>
                      <div style={{ fontSize: 9.5, color: "#5B616E", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{form.notes}</div>
                    </div>
                  )}
                </td>
                <td style={{ width: "44%", verticalAlign: "top" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
                    <tr><td style={sTotLbl}>Sub Total</td><td style={sTotVal}>{fmtIDR(totals.subtotal)}</td></tr>
                    {form.ppn_type === "ppn" && (
                      <tr><td style={sTotLbl}>VAT 11%</td><td style={sTotVal}>{fmtIDR(totals.ppn)}</td></tr>
                    )}
                    <tr>
                      <td style={{ fontSize: 14, fontWeight: 700, color: NAVY, borderTop: "1px solid #DDE2EC", paddingTop: 9 }}>Total</td>
                      <td style={{ fontSize: 16, fontWeight: 700, color: NAVY, textAlign: "right", whiteSpace: "nowrap", borderTop: "1px solid #DDE2EC", paddingTop: 9 }}>{fmtIDR(totals.grand)}</td>
                    </tr>
                  </tbody></table>
                  <div style={{ fontSize: 8.5, color: "#8A8F98", letterSpacing: 1.2, marginTop: 12 }}>TOTAL (IN WORDS)</div>
                  <div style={{ fontSize: 10, fontWeight: 700, lineHeight: 1.45, marginTop: 3 }}>{terbilang(totals.grand)} Rupiah</div>
                </td>
              </tr></tbody>
            </table>

            {/* Signature */}
            <div style={{ marginTop: 26, display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 255, display: "flex", alignItems: "center", gap: 4, background: "#F4F6FA", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ width: 82, flexShrink: 0 }}>
                  {qrDataUri
                    ? <img src={qrDataUri} alt="QR" style={{ width: 82, height: 82, display: "block" }} />
                    : <div style={{ width: 82, height: 82, border: "1px dashed #C7CCD6" }} />}
                </div>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "#14161F" }}>{salesName || "-"}</div>
                  <div style={{ fontSize: 9, color: "#8A8F98", marginTop: 2 }}>{salesJobTitle || "-"}</div>
                  {qrDataUri && (
                    <div style={{ fontSize: 7.5, color: "#8A8F98", letterSpacing: 0.8, marginTop: 7 }}>SCAN OR CLICK QR TO VERIFY</div>
                  )}
                </div>
              </div>
            </div>

            {/* Confidential notice — last page only in PDF; shown once here since preview is single-page */}
            <div style={{ marginTop: 24, padding: "16px 20px", background: "#F4F6FA", borderLeft: "3px solid #3B4A63", borderRadius: "0 12px 12px 0" }}>
              <div style={{ display: "inline-block", fontSize: 8, lineHeight: 1, fontWeight: 700, letterSpacing: 1.6, color: "#fff", background: "#3B4A63", padding: "5px 11px 4px", borderRadius: 20 }}>CONFIDENTIAL</div>
              <div style={{ fontSize: 8, color: "#5B616E", lineHeight: 1.65, marginTop: 8 }} data-testid="preview-footer-text">
                This quotation and its contents are confidential and intended solely for the named recipient.
                Any review, disclosure, copying or distribution without prior written consent of {LEGAL_NAME} is strictly prohibited.
              </div>
            </div>

            {/* Footer */}
            <div style={{ marginTop: "auto", paddingTop: 20 }}>
              <div style={{ borderTop: "1px solid #E7EAF0", paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#3B4A63", letterSpacing: 0.3 }}>Your Creative-Tech Partner</div>
                  <div style={{ fontSize: 7, fontWeight: 400, color: "#A0A3A8", marginTop: 2 }} data-testid="preview-footer-generated">Generated by IPTEC&reg; Supersales App.</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 8, color: "#3B4A63", fontWeight: 700, letterSpacing: 0.3 }}>{company?.web || "www.iptec.id"}</div>
                  <div style={{ fontSize: 7, fontWeight: 400, color: "#A0A3A8", marginTop: 2 }} data-testid="preview-footer-page">Page 1 of 1</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


export function InfoRow({ label, value, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#F4F5F7] last:border-0 gap-4">
      <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold shrink-0">{label}</span>
      <span className={`text-sm text-[#090808] font-semibold text-right ${valueClass}`}>{value}</span>
    </div>
  );
}

export function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[#3B4A63] mb-1 block">{label}{required && <span className="text-[#CE6968]"> *</span>}</span>
      {children}
    </label>
  );
}

export const inp = "w-full h-10 px-3 rounded-lg bg-white border border-[#DCE0E7] text-sm outline-none focus:ring-2 focus:ring-[#97ADD4]/40";
export const inpXs = "w-full h-8 px-2 rounded-md bg-white border border-[#DCE0E7] text-xs outline-none focus:ring-2 focus:ring-[#97ADD4]/40";
